import React, { createContext, useContext, useReducer, useState, useEffect, useRef } from 'react';
import { authService } from '../services/api';

const AuthContext = createContext();

const initialState = {
  user: null,
  token: null,
  loading: !!localStorage.getItem('lxi_token'),
  isAuthenticated: false,
  error: null,
};

const authReducer = (state, action) => {
  switch (action.type) {
    case 'AUTH_START':
      return { ...state, loading: true, error: null };

    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token || state.token,
        isAuthenticated: true,
        loading: false,
        error: null,
      };

    // Cache user data optimistically but keep loading=true until server confirms
    case 'OPTIMISTIC_AUTH':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token || state.token,
        isAuthenticated: false,
        loading: true,
        error: null,
      };

    case 'AUTH_ERROR':
      return { ...state, loading: false, error: action.payload, isAuthenticated: false };

    case 'LOGOUT':
      return { ...initialState, loading: false };

    case 'UPDATE_USER':
      return { ...state, user: { ...state.user, ...action.payload } };

    case 'SET_LOADING':
      return { ...state, loading: action.payload };

    default:
      return state;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const refreshTimer = useRef(null);

  const triggerLoginModal = () => setIsLoginModalOpen(true);
  const closeLoginModal = () => setIsLoginModalOpen(false);

  // Schedule silent token refresh ~1 min before expiry (15-min tokens → refresh at 14 min)
  const scheduleRefresh = () => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    refreshTimer.current = setTimeout(async () => {
      try {
        const resp = await authService.refresh();
        const { access_token } = resp.data;
        if (access_token) localStorage.setItem('lxi_token', access_token);
        scheduleRefresh();
      } catch {
        // Refresh failed — session ended; redirect handled by api.js interceptor
      }
    }, 14 * 60 * 1000); // 14 minutes
  };

  // Restore session on mount
  useEffect(() => {
    const token = localStorage.getItem('lxi_token');
    const storedUser = localStorage.getItem('lxi_user');

    const restoreSession = async () => {
      if (!token) return;

      try {
        // Cache user data while server verifies — keep loading=true to block PublicRoute redirect
        if (storedUser) {
          dispatch({
            type: 'OPTIMISTIC_AUTH',
            payload: { token, user: JSON.parse(storedUser) },
          });
        }
        // Verify with the server (uses HttpOnly cookie)
        const response = await authService.getCurrentUser();
        const freshUser = response.data;
        localStorage.setItem('lxi_user', JSON.stringify(freshUser));
        dispatch({ type: 'AUTH_SUCCESS', payload: { token, user: freshUser } });
        scheduleRefresh();
      } catch {
        localStorage.removeItem('lxi_token');
        localStorage.removeItem('lxi_user');
        dispatch({ type: 'LOGOUT' });
      }
    };

    restoreSession();

    return () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── register ────────────────────────────────────────────────────────────
  // No modifica estado global: register no crea sesión.
  // El componente maneja su propio isLoading local.
  const register = async (registerData) => {
    try {
      const response = await authService.register(registerData);
      const { user } = response.data;
      return { success: true, user };
    } catch (error) {
      const msg = error.response?.data?.detail || error.message || 'Registration failed';
      const status = error.response?.status;
      return { success: false, error: msg, status };
    }
  };

  // ── login ────────────────────────────────────────────────────────────────
  const login = async (email, password) => {
    dispatch({ type: 'AUTH_START' });
    try {
      const response = await authService.login({ email, password });
      const { access_token, user } = response.data;
      if (access_token) localStorage.setItem('lxi_token', access_token);
      localStorage.setItem('lxi_user', JSON.stringify(user));
      dispatch({ type: 'AUTH_SUCCESS', payload: { token: access_token, user } });
      await refreshUser();
      scheduleRefresh();
      return { success: true, user };
    } catch (error) {
      if (error.response?.status === 429) {
        const detail = error.response.data?.detail || 'Cuenta bloqueada temporalmente.';
        dispatch({ type: 'AUTH_ERROR', payload: detail });
        return { success: false, locked: true, error: detail };
      }
      const msg = error.response?.data?.detail || 'Credenciales inválidas o acceso no disponible.';
      dispatch({ type: 'AUTH_ERROR', payload: msg });
      return { success: false, error: msg };
    }
  };

  // ── logout ───────────────────────────────────────────────────────────────
  const logout = async () => {
    try {
      await authService.logout();
    } catch {
      // Silent — always clear local state
    }
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    localStorage.removeItem('lxi_token');
    localStorage.removeItem('lxi_user');
    dispatch({ type: 'LOGOUT' });
  };

  // ── updateUser ───────────────────────────────────────────────────────────
  const updateUser = (userData) => {
    const updated = { ...state.user, ...userData };
    localStorage.setItem('lxi_user', JSON.stringify(updated));
    dispatch({ type: 'UPDATE_USER', payload: userData });
  };

  const refreshUser = async () => {
    try {
      const token = localStorage.getItem('lxi_token');
      if (!token) return;
      const response = await authService.getCurrentUser();
      const freshUser = response.data;
      localStorage.setItem('lxi_user', JSON.stringify(freshUser));
      dispatch({ type: 'AUTH_SUCCESS', payload: { token, user: freshUser } });
    } catch (error) {
      console.error('Error refreshing user:', error);
    }
  };

  // ── forgotPassword ───────────────────────────────────────────────────────
  const forgotPassword = async (email) => {
    try {
      const response = await authService.forgotPassword(email);
      return { success: true, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Ocurrió un error. Inténtalo de nuevo.',
      };
    }
  };

  // ── verifyEmail ──────────────────────────────────────────────────────────
  const verifyEmail = async (email, otp, token) => {
    try {
      const payload = token ? { token } : { email, otp };
      const response = await authService.verifyEmail(payload);
      const { access_token, user } = response.data;
      if (access_token) localStorage.setItem('lxi_token', access_token);
      localStorage.setItem('lxi_user', JSON.stringify(user));
      dispatch({ type: 'AUTH_SUCCESS', payload: { token: access_token, user } });
      scheduleRefresh();
      return { success: true, user };
    } catch (error) {
      const msg = error.response?.data?.detail || 'Código inválido o expirado.';
      return { success: false, error: msg, status: error.response?.status };
    }
  };

  // ── verifyUnlock ─────────────────────────────────────────────────────────
  const verifyUnlock = async (email, otp) => {
    try {
      const response = await authService.verifyUnlock(email, otp);
      return { success: true, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Código inválido o expirado.',
      };
    }
  };

  const value = {
    ...state,
    register,
    login,
    logout,
    updateUser,
    refreshUser,
    forgotPassword,
    verifyEmail,
    verifyUnlock,
    isLoginModalOpen,
    triggerLoginModal,
    closeLoginModal,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
